import type {
  MeetingTemplateColor,
  MeetingTemplateKind,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateDraft,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';
import {
  AUDIENCE_NONE,
  audienceChoice,
  audienceIds,
} from './template-audience-options.ts';
import type { KeyedTopic } from './template-topics.tsx';

/** The template form while it is being written. */
export interface TemplateDraftState {
  name: string;
  color: MeetingTemplateColor;
  /** Only set when the editor was given a kind. */
  kind?: MeetingTemplateKind;
  /** Only set when the editor offers who the template is for. */
  scope?: MeetingTemplateScope;
  description: string;
  /** A value of the audience dropdown, see `audienceChoice`. */
  audience: string;
  topics: KeyedTopic[];
}

export function toTemplateDraftState(
  template: MeetingTemplateDraft | null,
  kind: MeetingTemplateKind | undefined,
  scope: MeetingTemplateScope | undefined,
): TemplateDraftState {
  return {
    name: template?.name ?? '',
    color: template?.color ?? 'navy',
    kind,
    scope,
    description: template?.description ?? '',
    audience: template ? audienceChoice(template) : AUDIENCE_NONE,
    topics: (template?.sections ?? [{ title: '', description: '' }]).map(
      (section, key) => ({ ...section, key }),
    ),
  };
}

/* A template only its writer sees has nobody to be aimed at. */
export function offersAudience(
  draft: TemplateDraftState,
  tracks: MeetingTemplateTrackOption[],
  cohorts: MeetingTemplateCohortOption[],
): boolean {
  return (tracks.length > 0 || cohorts.length > 0) && draft.scope !== 'personal';
}

export function withAddedTopic(topics: KeyedTopic[]): KeyedTopic[] {
  return [
    ...topics,
    {
      title: '',
      description: '',
      key: topics.reduce((max, topic) => Math.max(max, topic.key), -1) + 1,
    },
  ];
}

/** A name and a title on every topic: enough to save. */
export function isTemplateDraftComplete(draft: TemplateDraftState): boolean {
  return (
    draft.name.trim().length > 0 &&
    draft.topics.length > 0 &&
    draft.topics.every((topic) => topic.title.trim().length > 0)
  );
}

/** The draft as the host saves it. The audience goes along only when it was asked. */
export function toMeetingTemplateDraft(
  draft: TemplateDraftState,
  { withAudience }: { withAudience: boolean },
): MeetingTemplateDraft {
  return {
    name: draft.name.trim(),
    color: draft.color,
    description: draft.description.trim() || null,
    sections: draft.topics.map((topic) => ({
      title: topic.title,
      description: topic.description,
    })),
    ...(draft.kind && { kind: draft.kind }),
    ...(draft.scope && { scope: draft.scope }),
    ...(withAudience && audienceIds(draft.audience)),
  };
}
